import { useState, useEffect, useRef } from 'react'
import { Folder, Zap, FileText, ArrowLeft, Loader, MessageSquarePlus, Plus, X, Upload, Check, BookOpen, FileUp, ClipboardList, Trash2, Save, Edit2, Copy, Lock, StickyNote, Download } from 'lucide-react'
import api from '../services/api'

interface ProjectDetailPageProps {
  projectName: string
  onBack: () => void
  onNewConversation?: (projectName: string) => void
}

interface ProjectConfig {
  name?: string
  description?: string
  skills_enabled?: string[]
  context_file?: string
}

interface Skill {
  id: string
  name: string
  description?: string
  category?: string
}

interface ContextBundle {
  project: string
  config: ProjectConfig
  context_content: string
  context_file: string
  skills_manifest: Skill[]
  skills_count: number
  filtered: boolean
}

interface JournalEntry {
  date: string
  titre: string
  statut: 'en_cours' | 'terminé' | 'bloqué'
  slug: string
}

type TabType = 'skills' | 'context' | 'journal' | 'notes'
type ModalTab = 'library' | 'import'

export default function ProjectDetailPage({ projectName, onBack, onNewConversation }: ProjectDetailPageProps) {
  const [contextBundle, setContextBundle] = useState<ContextBundle | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<TabType>('skills')

  // Modale ajouter skill
  const [showSkillModal, setShowSkillModal] = useState(false)
  const [modalTab, setModalTab] = useState<ModalTab>('library')
  const [availableSkills, setAvailableSkills] = useState<Skill[]>([])
  const [selectedSkillIds, setSelectedSkillIds] = useState<Set<string>>(new Set())
  const [loadingSkills, setLoadingSkills] = useState(false)

  // Import skill
  const [importFile, setImportFile] = useState<File | null>(null)
  const [importName, setImportName] = useState('')
  const [importCategory, setImportCategory] = useState('Système')
  const [importing, setImporting] = useState(false)

  // Journal
  const [journalEntries, setJournalEntries] = useState<JournalEntry[]>([])
  const [showNewEntryForm, setShowNewEntryForm] = useState(false)
  const [newEntry, setNewEntry] = useState({ titre: '', statut: 'en_cours' as const, contenu: '' })
  const [selectedEntry, setSelectedEntry] = useState<{ entry: JournalEntry, contenu: string } | null>(null)
  const [creatingEntry, setCreatingEntry] = useState(false)

  // Bloc-notes
  const [notesContent, setNotesContent] = useState('')
  const [notesLoading, setNotesLoading] = useState(false)
  const [notesSaving, setNotesSaving] = useState(false)
  const [notesStatus, setNotesStatus] = useState<'success' | 'error' | ''>('')

  // Skill content viewer + editor
  const [expandedSkill, setExpandedSkill] = useState<{ id: string; name: string; content: string } | null>(null)
  const [loadingSkillContent, setLoadingSkillContent] = useState<string | null>(null)
  const [skillEditMode, setSkillEditMode] = useState(false)
  const [skillEditContent, setSkillEditContent] = useState('')
  const [savingSkillContent, setSavingSkillContent] = useState(false)
  const [skillSaveStatus, setSkillSaveStatus] = useState<'success' | 'error' | ''>('')

  const handleReadSkill = async (skill: Skill, e: React.MouseEvent) => {
    e.stopPropagation()
    if (loadingSkillContent === skill.id) return
    try {
      setLoadingSkillContent(skill.id)
      const { data } = await api.get(`/api/skills/${skill.id}`)
      setExpandedSkill({ id: skill.id, name: skill.name, content: data.content || 'Aucun contenu' })
      setSkillEditMode(false)
      setSkillEditContent(data.content || '')
    } catch {
      setExpandedSkill({ id: skill.id, name: skill.name, content: '⚠️ Impossible de charger le contenu.' })
    } finally {
      setLoadingSkillContent(null)
    }
  }

  const handleSaveSkillContent = async () => {
    if (!expandedSkill) return
    setSavingSkillContent(true)
    try {
      await api.put(`/api/skills/${expandedSkill.id}`, { content: skillEditContent })
      setExpandedSkill({ ...expandedSkill, content: skillEditContent })
      setSkillEditMode(false)
      setSkillSaveStatus('success')
      setTimeout(() => setSkillSaveStatus(''), 3000)
    } catch {
      setSkillSaveStatus('error')
      setTimeout(() => setSkillSaveStatus(''), 3000)
    } finally {
      setSavingSkillContent(false)
    }
  }

  // File Explorer State
  const [projectFiles, setProjectFiles] = useState<{ path: string; size: number; modified: number }[]>([])
  const [selectedFilePath, setSelectedFilePath] = useState<string | null>(null)
  const [fileContent, setFileContent] = useState('')
  const [loadingFileContent, setLoadingFileContent] = useState(false)
  const [savingFile, setSavingFile] = useState(false)
  const [deletingFile, setDeletingFile] = useState<string | null>(null)
  const [newFileName, setNewFileName] = useState('')
  const [showNewFileInput, setShowNewFileNameInput] = useState(false)
  const [fileStatus, setFileStatus] = useState<{ type: 'success' | 'error' | ''; message: string }>({ type: '', message: '' })
  // Import / téléchargement / aperçu binaire
  const fileInputRef = useRef<HTMLInputElement>(null)
  const uploadToKnowledgeRef = useRef(false)
  const [uploading, setUploading] = useState(false)
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [previewKind, setPreviewKind] = useState<'image' | 'pdf' | 'text' | null>(null)
  const [extractedText, setExtractedText] = useState('')

  // Actions projet
  const [isEditing, setIsEditing] = useState(false)
  const [editForm, setEditForm] = useState({ name: '', description: '', category_id: '' })
  const [isDuplicating, setIsDuplicating] = useState(false)
  const [dupName, setDupName] = useState('')
  const [actionLoading, setActionLoading] = useState(false)
  const [categories, setCategories] = useState<{ id: string; name: string; icon: string }[]>([])

  useEffect(() => {
    fetchProjectContext()
    fetchCategories()
    if (activeTab === 'journal') {
      fetchJournal()
    } else if (activeTab === 'context') {
      fetchProjectFiles()
    }
  }, [projectName])

  useEffect(() => {
    if (activeTab === 'journal') {
      fetchJournal()
    } else if (activeTab === 'context') {
      fetchProjectFiles()
    } else if (activeTab === 'notes') {
      fetchNotes()
    }
  }, [activeTab])

  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/api/categories')
      setCategories(data)
    } catch {}
  }

  const fetchNotes = async () => {
    try {
      setNotesLoading(true)
      const { data } = await api.get(`/api/projects/${projectName}/files/notes.md`)
      setNotesContent(data.content || '')
    } catch {
      setNotesContent('')
    } finally {
      setNotesLoading(false)
    }
  }

  const saveNotes = async () => {
    try {
      setNotesSaving(true)
      await api.put(`/api/projects/${projectName}/files/notes.md`, { content: notesContent })
      setNotesStatus('success')
      setTimeout(() => setNotesStatus(''), 3000)
    } catch {
      setNotesStatus('error')
      setTimeout(() => setNotesStatus(''), 3000)
    } finally {
      setNotesSaving(false)
    }
  }

  const openEdit = () => {
    setEditForm({
      name: contextBundle?.config.name || contextBundle?.project || '',
      description: contextBundle?.config.description || '',
      category_id: (contextBundle?.config as any)?.category_id || ''
    })
    setIsEditing(true)
    setIsDuplicating(false)
  }

  const handleSaveEdit = async () => {
    try {
      setActionLoading(true)
      await api.put(`/api/projects/${projectName}/config`, {
        ...contextBundle?.config,
        name: editForm.name,
        description: editForm.description,
        category_id: editForm.category_id || null
      })
      setIsEditing(false)
      await fetchProjectContext()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors de la modification')
    } finally {
      setActionLoading(false)
    }
  }

  const handleDeleteProject = async () => {
    if (!confirm(`Supprimer "${projectName}" ? Cette action est IRRÉVERSIBLE.`)) return
    if (!confirm(`Dernière confirmation : supprimer définitivement "${projectName}" ?`)) return
    try {
      setActionLoading(true)
      await api.delete(`/api/projects/${projectName}`)
      onBack()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors de la suppression')
      setActionLoading(false)
    }
  }

  const startDuplicate = () => {
    setDupName(projectName + '-copy')
    setIsDuplicating(true)
    setIsEditing(false)
  }

  const handleDuplicate = async () => {
    if (!dupName.trim() || !/^[a-z0-9_-]+$/.test(dupName)) {
      alert('Nom invalide : minuscules, chiffres, tirets uniquement')
      return
    }
    try {
      setActionLoading(true)
      await api.post(`/api/projects/${projectName}/duplicate?new_name=${dupName}`)
      setIsDuplicating(false)
      onBack()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors de la duplication')
      setActionLoading(false)
    }
  }

  const fetchProjectContext = async () => {
    try {
      setLoading(true)
      setError(null)
      const { data } = await api.get<ContextBundle>(`/api/projects/${projectName}/load-context`)
      setContextBundle(data)
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Erreur lors du chargement du projet')
    } finally {
      setLoading(false)
    }
  }

  const fetchProjectFiles = async () => {
    try {
      const { data } = await api.get<{ path: string; size: number; modified: number }[]>(`/api/projects/${projectName}/files`)
      setProjectFiles(data)
    } catch (err) {
      console.error('Erreur chargement fichiers projet:', err)
    }
  }

  const TEXT_EXTENSIONS = ['.md', '.json', '.txt', '.yaml', '.yml', '.log', '.csv', '.html', '.htm']
  const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  const extOf = (path: string) => '.' + (path.split('.').pop()?.toLowerCase() || '')
  const isTextFile = (path: string) => TEXT_EXTENSIONS.includes(extOf(path))

  const clearPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl)
    setPreviewUrl(null)
    setPreviewKind(null)
    setExtractedText('')
  }

  const handleSelectFile = async (path: string) => {
    setSelectedFilePath(path)
    setFileStatus({ type: '', message: '' })
    clearPreview()
    const ext = extOf(path)

    // Fichier texte éditable → éditeur classique
    if (isTextFile(path)) {
      try {
        setLoadingFileContent(true)
        const { data } = await api.get<{ path: string; content: string }>(`/api/projects/${projectName}/files/${path}`)
        setFileContent(data.content || '')
      } catch (err: any) {
        console.error('Erreur lecture fichier:', err)
        alert(err.response?.data?.detail || 'Erreur lors de la lecture du fichier')
      } finally {
        setLoadingFileContent(false)
      }
      return
    }

    setFileContent('')

    // Image ou PDF → aperçu visuel (blob via axios pour porter le token JWT)
    if (IMAGE_EXTENSIONS.includes(ext) || ext === '.pdf') {
      try {
        setLoadingFileContent(true)
        const res = await api.get(`/api/projects/${projectName}/download/${path}`, { responseType: 'blob' })
        setPreviewUrl(URL.createObjectURL(res.data))
        setPreviewKind(ext === '.pdf' ? 'pdf' : 'image')
      } catch (err: any) {
        console.error('Erreur aperçu fichier:', err)
        alert(err.response?.data?.detail || "Erreur lors de l'aperçu du fichier")
      } finally {
        setLoadingFileContent(false)
      }
      return
    }

    // Autre doc (docx, xlsx, html…) → extraction du texte pour lecture
    try {
      setLoadingFileContent(true)
      const { data } = await api.get<{ text: string }>(`/api/projects/${projectName}/extract/${path}`)
      setExtractedText(data.text || '(aucun texte extrait)')
      setPreviewKind('text')
    } catch (err: any) {
      console.error('Erreur extraction fichier:', err)
      setExtractedText('')
      setPreviewKind('text')
      setFileStatus({ type: 'error', message: err.response?.data?.detail || "Lecture impossible pour ce fichier" })
    } finally {
      setLoadingFileContent(false)
    }
  }

  const triggerImport = (toKnowledge: boolean) => {
    uploadToKnowledgeRef.current = toKnowledge
    fileInputRef.current?.click()
  }

  const handleFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = '' // permet de re-sélectionner le même fichier
    if (!file) return

    const toKnowledge = uploadToKnowledgeRef.current
    try {
      setUploading(true)
      setFileStatus({ type: '', message: '' })
      const form = new FormData()
      form.append('file', file)
      if (toKnowledge) form.append('to_knowledge', 'true')
      const { data } = await api.post(`/api/projects/${projectName}/upload`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      let msg = `« ${file.name} » importé dans le projet`
      if (toKnowledge) {
        const k = data.knowledge
        msg += k?.chunks != null ? ` + Knowledge (${k.chunks} chunks)` : (k?.error ? ` (Knowledge : ${k.error})` : '')
      }
      setFileStatus({ type: 'success', message: msg })
      setTimeout(() => setFileStatus({ type: '', message: '' }), 5000)
      fetchProjectFiles()
    } catch (err: any) {
      console.error('Erreur import fichier:', err)
      setFileStatus({ type: 'error', message: err.response?.data?.detail || "Erreur lors de l'import du fichier" })
    } finally {
      setUploading(false)
    }
  }

  const handleDownloadFile = async (path: string, e: React.MouseEvent) => {
    e.stopPropagation()
    try {
      setDownloadingFile(path)
      const res = await api.get(`/api/projects/${projectName}/download/${path}`, { responseType: 'blob' })
      const url = URL.createObjectURL(res.data)
      const a = document.createElement('a')
      a.href = url
      a.download = path.split('/').pop() || path
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch (err: any) {
      console.error('Erreur téléchargement:', err)
      alert(err.response?.data?.detail || 'Erreur lors du téléchargement')
    } finally {
      setDownloadingFile(null)
    }
  }

  const handleSaveFile = async () => {
    if (!selectedFilePath) return
    try {
      setSavingFile(true)
      setFileStatus({ type: '', message: '' })
      await api.put(`/api/projects/${projectName}/files/${selectedFilePath}`, { content: fileContent })
      setFileStatus({ type: 'success', message: 'Fichier enregistré avec succès !' })
      setTimeout(() => setFileStatus({ type: '', message: '' }), 3000)
      fetchProjectFiles()
    } catch (err: any) {
      console.error('Erreur sauvegarde fichier:', err)
      setFileStatus({ type: 'error', message: err.response?.data?.detail || 'Erreur lors de la sauvegarde du fichier' })
    } finally {
      setSavingFile(false)
    }
  }

  const handleDeleteFile = async (path: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!window.confirm(`Êtes-vous sûr de vouloir supprimer définitivement le fichier ${path} ?`)) {
      return
    }
    try {
      setDeletingFile(path)
      await api.delete(`/api/projects/${projectName}/files/${path}`)
      if (selectedFilePath === path) {
        setSelectedFilePath(null)
        setFileContent('')
      }
      fetchProjectFiles()
    } catch (err: any) {
      console.error('Erreur suppression fichier:', err)
      alert(err.response?.data?.detail || 'Erreur lors de la suppression du fichier')
    } finally {
      setDeletingFile(null)
    }
  }

  const handleCreateFile = async () => {
    if (!newFileName.trim()) return
    const ext = '.' + newFileName.split('.').pop()?.toLowerCase()
    const allowed = ['.md', '.json', '.txt', '.yaml', '.yml', '.log']
    if (!allowed.includes(ext)) {
      alert(`Extension non autorisée. Les extensions autorisées sont : ${allowed.join(', ')}`)
      return
    }

    try {
      await api.put(`/api/projects/${projectName}/files/${newFileName}`, { content: `# ${newFileName}\n\n` })
      setNewFileName('')
      setShowNewFileNameInput(false)
      fetchProjectFiles()
      handleSelectFile(newFileName)
    } catch (err: any) {
      console.error('Erreur création fichier:', err)
      alert(err.response?.data?.detail || 'Erreur lors de la création du fichier')
    }
  }

  const fetchAvailableSkills = async () => {
    try {
      setLoadingSkills(true)
      const { data } = await api.get<Skill[]>('/api/projects/available-skills')
      setAvailableSkills(data)
    } catch (err) {
      console.error('Erreur chargement skills:', err)
    } finally {
      setLoadingSkills(false)
    }
  }

  useEffect(() => {
    if (activeTab === 'skills' && availableSkills.length === 0) {
      fetchAvailableSkills()
    }
  }, [activeTab])

  const toggleSkill = async (skillId: string) => {
    if (!contextBundle) return
    const current = new Set(contextBundle.config.skills_enabled || [])
    if (current.has(skillId)) {
      current.delete(skillId)
    } else {
      current.add(skillId)
    }
    try {
      await api.put(`/api/projects/${projectName}/config`, {
        ...contextBundle.config,
        skills_enabled: Array.from(current)
      })
      await fetchProjectContext()
    } catch (err) {
      console.error('Erreur toggle skill:', err)
    }
  }

  const fetchJournal = async () => {
    try {
      const { data } = await api.get<JournalEntry[]>(`/api/projects/${projectName}/journal`)
      setJournalEntries(data)
    } catch (err) {
      console.error('Erreur chargement journal:', err)
      setJournalEntries([])
    }
  }

  const handleOpenSkillModal = () => {
    setShowSkillModal(true)
    setModalTab('library')
    setSelectedSkillIds(new Set(contextBundle?.config.skills_enabled || []))
    fetchAvailableSkills()
  }

  const handleCloseSkillModal = () => {
    setShowSkillModal(false)
    setImportFile(null)
    setImportName('')
    setImportCategory('Système')
  }

  const handleAddSkills = async () => {
    try {
      setLoadingSkills(true)
      const updatedConfig = {
        ...contextBundle?.config,
        skills_enabled: Array.from(selectedSkillIds)
      }
      await api.put(`/api/projects/${projectName}/config`, updatedConfig)
      await fetchProjectContext()
      handleCloseSkillModal()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors de la mise à jour')
    } finally {
      setLoadingSkills(false)
    }
  }

  const handleImportSkill = async () => {
    if (!importFile || !importName) {
      alert('Fichier et nom requis')
      return
    }

    try {
      setImporting(true)
      const formData = new FormData()
      formData.append('file', importFile)
      formData.append('name', importName)
      formData.append('category', importCategory)

      const { data: newSkill } = await api.post('/api/skills', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      })

      // Ajouter automatiquement à skills_enabled
      const updatedSkills = [...(contextBundle?.config.skills_enabled || []), newSkill.id]
      const updatedConfig = {
        ...contextBundle?.config,
        skills_enabled: updatedSkills
      }

      await api.put(`/api/projects/${projectName}/config`, updatedConfig)
      await fetchProjectContext()
      handleCloseSkillModal()
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors de l\'import')
    } finally {
      setImporting(false)
    }
  }

  const handleCreateJournalEntry = async () => {
    if (!newEntry.titre || !newEntry.contenu) {
      alert('Titre et contenu requis')
      return
    }

    try {
      setCreatingEntry(true)
      await api.post(`/api/projects/${projectName}/journal`, newEntry)
      await fetchJournal()
      setNewEntry({ titre: '', statut: 'en_cours', contenu: '' })
      setShowNewEntryForm(false)
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors de la création')
    } finally {
      setCreatingEntry(false)
    }
  }

  const handleSelectJournalEntry = async (entry: JournalEntry) => {
    try {
      const { data } = await api.get(`/api/projects/${projectName}/journal/${entry.slug}`)
      setSelectedEntry({ entry, contenu: data.contenu })
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Erreur lors du chargement')
    }
  }

  const getStatusBadge = (statut: string) => {
    const styles = {
      en_cours: { bg: '#1e3a8a', color: '#93c5fd', label: 'En cours' },
      terminé: { bg: '#14532d', color: '#86efac', label: 'Terminé' },
      bloqué: { bg: '#7f1d1d', color: '#fca5a5', label: 'Bloqué' }
    }
    const style = styles[statut as keyof typeof styles] || styles.en_cours
    return (
      <span style={{
        backgroundColor: style.bg,
        color: style.color,
        padding: '4px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: '500'
      }}>
        {style.label}
      </span>
    )
  }

  if (loading) {
    return (
      <div style={{ backgroundColor: '#1f2937', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Loader size={32} style={{ color: '#ec4899', animation: 'spin 1s linear infinite' }} />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    )
  }

  if (error || !contextBundle) {
    return (
      <div style={{ backgroundColor: '#1f2937', height: '100%', padding: '24px' }}>
        <button onClick={onBack} style={{ backgroundColor: 'transparent', border: 'none', color: '#ec4899', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '24px', padding: '8px 0' }}>
          <ArrowLeft size={18} /> Retour aux projets
        </button>
        <div style={{ backgroundColor: '#7f1d1d', border: '1px solid #991b1b', borderRadius: '8px', padding: '16px', color: '#fca5a5' }}>
          {error || 'Projet non trouvé'}
        </div>
      </div>
    )
  }

  return (
    <div style={{ backgroundColor: '#1f2937', height: '100%', overflowY: 'auto', padding: '24px' }}>
      <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
        {/* Bouton retour */}
        <button onClick={onBack} style={{ backgroundColor: 'transparent', border: 'none', color: '#ec4899', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '14px', marginBottom: '24px', padding: '8px 0', transition: 'opacity 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'} onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}>
          <ArrowLeft size={18} /> Retour aux projets
        </button>

        {/* Header du projet */}
        <div style={{ backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '12px', padding: '24px', marginBottom: '24px' }}>
          {/* Nom + 3 boutons */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <Folder size={32} style={{ color: '#ec4899', flexShrink: 0 }} />
            <h1 style={{ fontSize: '26px', fontWeight: 'bold', color: '#ffffff', margin: 0, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {contextBundle.config.name || contextBundle.project}
            </h1>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button onClick={openEdit} disabled={actionLoading}
                style={{ background: isEditing ? '#4b5563' : '#2d3748', border: '1px solid #4b5563', borderRadius: '6px', color: '#d1d5db', cursor: 'pointer', padding: '7px 13px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}>
                <Edit2 size={14} /> Modifier
              </button>
              <button onClick={startDuplicate} disabled={actionLoading}
                style={{ background: isDuplicating ? '#4b5563' : '#2d3748', border: '1px solid #4b5563', borderRadius: '6px', color: '#d1d5db', cursor: 'pointer', padding: '7px 13px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}>
                <Copy size={14} /> Dupliquer
              </button>
              <button onClick={handleDeleteProject} disabled={actionLoading}
                style={{ background: '#2d3748', border: '1px solid #4b5563', borderRadius: '6px', color: '#ef4444', cursor: 'pointer', padding: '7px 13px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', fontWeight: '500' }}
                onMouseEnter={e => { e.currentTarget.style.background = '#7f1d1d'; e.currentTarget.style.borderColor = '#ef4444' }}
                onMouseLeave={e => { e.currentTarget.style.background = '#2d3748'; e.currentTarget.style.borderColor = '#4b5563' }}>
                <Trash2 size={14} /> Supprimer
              </button>
            </div>
          </div>

          {contextBundle.config.description && (
            <p style={{ color: '#d1d5db', fontSize: '15px', margin: '0 0 12px 0', lineHeight: '1.5' }}>
              {contextBundle.config.description}
            </p>
          )}
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', fontSize: '13px', color: '#9ca3af' }}>
            <span>{contextBundle.skills_count} skill(s)</span>
            <span>•</span>
            <span>{contextBundle.filtered ? 'Mode filtré' : 'Toutes les skills'}</span>
            {onNewConversation && (
              <button onClick={() => onNewConversation(projectName)} style={{ marginLeft: 'auto', backgroundColor: '#ec4899', border: 'none', borderRadius: '8px', color: '#ffffff', cursor: 'pointer', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', fontWeight: '500' }}>
                <MessageSquarePlus size={16} /> Nouvelle conversation
              </button>
            )}
          </div>

          {/* Panneau Modifier */}
          {isEditing && (
            <div style={{ marginTop: '16px', borderTop: '1px solid #4b5563', paddingTop: '16px', display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr 1fr', gap: '10px' }}>
                <div>
                  <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Nom</label>
                  <input value={editForm.name} onChange={e => setEditForm({ ...editForm, name: e.target.value })}
                    style={{ width: '100%', background: '#1f2937', border: '1px solid #4b5563', borderRadius: '6px', padding: '7px 10px', color: '#fff', fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Description</label>
                  <input value={editForm.description} onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                    style={{ width: '100%', background: '#1f2937', border: '1px solid #4b5563', borderRadius: '6px', padding: '7px 10px', color: '#9ca3af', fontSize: '13px', boxSizing: 'border-box' }} />
                </div>
                <div>
                  <label style={{ color: '#9ca3af', fontSize: '12px', display: 'block', marginBottom: '4px' }}>Catégorie</label>
                  <select value={editForm.category_id} onChange={e => setEditForm({ ...editForm, category_id: e.target.value })}
                    style={{ width: '100%', background: '#1f2937', border: '1px solid #4b5563', borderRadius: '6px', padding: '7px 10px', color: '#9ca3af', fontSize: '13px', boxSizing: 'border-box' }}>
                    <option value="">— Sans catégorie —</option>
                    {categories.map(cat => <option key={cat.id} value={cat.id}>{cat.icon} {cat.name}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button onClick={handleSaveEdit} disabled={actionLoading}
                  style={{ padding: '7px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', opacity: actionLoading ? 0.5 : 1 }}>
                  <Save size={14} /> Sauvegarder
                </button>
                <button onClick={() => setIsEditing(false)}
                  style={{ padding: '7px 10px', background: '#374151', color: '#9ca3af', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                  <X size={14} />
                </button>
              </div>
            </div>
          )}

          {/* Panneau Dupliquer */}
          {isDuplicating && (
            <div style={{ marginTop: '16px', borderTop: '1px solid #4b5563', paddingTop: '16px', display: 'flex', gap: '8px', alignItems: 'center' }}>
              <input autoFocus value={dupName} onChange={e => setDupName(e.target.value.toLowerCase())}
                onKeyDown={e => e.key === 'Enter' && handleDuplicate()}
                placeholder="nouveau-nom"
                style={{ flex: 1, background: '#1f2937', border: '1px solid #4b5563', borderRadius: '6px', padding: '7px 10px', color: '#fff', fontSize: '13px' }} />
              <button onClick={handleDuplicate} disabled={actionLoading}
                style={{ padding: '7px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px', opacity: actionLoading ? 0.5 : 1 }}>
                <Copy size={14} /> OK
              </button>
              <button onClick={() => { setIsDuplicating(false); setDupName('') }}
                style={{ padding: '7px 10px', background: '#374151', color: '#9ca3af', border: 'none', borderRadius: '6px', cursor: 'pointer' }}>
                <X size={14} />
              </button>
            </div>
          )}
        </div>

        {/* Onglets */}
        <div style={{ marginBottom: '24px', display: 'flex', gap: '8px', borderBottom: '1px solid #4b5563' }}>
          <button onClick={() => setActiveTab('skills')} style={{ backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === 'skills' ? '2px solid #ec4899' : '2px solid transparent', color: activeTab === 'skills' ? '#ec4899' : '#9ca3af', cursor: 'pointer', padding: '12px 20px', fontSize: '15px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
            <Zap size={18} /> Skills activées
          </button>
          <button onClick={() => setActiveTab('context')} style={{ backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === 'context' ? '2px solid #ec4899' : '2px solid transparent', color: activeTab === 'context' ? '#ec4899' : '#9ca3af', cursor: 'pointer', padding: '12px 20px', fontSize: '15px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
            <Folder size={18} /> 📁 Fichiers
          </button>
          <button onClick={() => setActiveTab('journal')} style={{ backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === 'journal' ? '2px solid #ec4899' : '2px solid transparent', color: activeTab === 'journal' ? '#ec4899' : '#9ca3af', cursor: 'pointer', padding: '12px 20px', fontSize: '15px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
            <ClipboardList size={18} /> Journal
          </button>
          <button onClick={() => setActiveTab('notes')} style={{ backgroundColor: 'transparent', border: 'none', borderBottom: activeTab === 'notes' ? '2px solid #ec4899' : '2px solid transparent', color: activeTab === 'notes' ? '#ec4899' : '#9ca3af', cursor: 'pointer', padding: '12px 20px', fontSize: '15px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '8px', transition: 'all 0.2s' }}>
            <StickyNote size={18} /> Bloc-notes
          </button>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'skills' && (
          <div style={{ backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '12px', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Zap size={22} style={{ color: '#ec4899' }} /> Skills
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <span style={{ color: '#9ca3af', fontSize: '13px' }}>
                  {(contextBundle.config.skills_enabled || []).length} activée{(contextBundle.config.skills_enabled || []).length > 1 ? 's' : ''} / {availableSkills.length}
                </span>
                <button onClick={handleOpenSkillModal} style={{ backgroundColor: '#4b5563', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: 'pointer', padding: '6px 12px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                  <Upload size={14} /> Importer
                </button>
              </div>
            </div>

            {loadingSkills ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                <Loader size={24} color="#ec4899" style={{ animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                {(() => {
                  const SOCLE_IDS = new Set([
                    'af0c4b36-756e-43a6-a234-0e2344b7cc44',
                    'protocole-de-blocage-standard-001',
                    'bcf6f082-c3b1-43f4-a219-d010d216e5c1',
                    '82b91534-caa0-4a94-a554-1f861ff93698',
                    '47971a0e-6b0b-42a1-b435-465b6892400b',
                    'a48df8e4-bd1b-495f-a9b0-45d5e307be8b'
                  ])
                  const enabledIds = new Set(contextBundle.config.skills_enabled || [])
                  const socleSkills = availableSkills.filter(s => SOCLE_IDS.has(s.id))
                  const projectSkills = availableSkills.filter(s => !SOCLE_IDS.has(s.id))
                  const grouped = projectSkills.reduce((acc, skill) => {
                    const cat = skill.category || 'Général'
                    if (!acc[cat]) acc[cat] = []
                    acc[cat].push(skill)
                    return acc
                  }, {} as Record<string, Skill[]>)

                  const renderSkillRow = (skill: Skill, locked: boolean) => {
                    const isEnabled = locked || enabledIds.has(skill.id)
                    return (
                      <div key={skill.id} style={{ backgroundColor: isEnabled ? '#1a2e1a' : '#1f2937', border: `1px solid ${isEnabled ? (locked ? '#f59e0b' : '#10b981') : '#4b5563'}`, borderRadius: '8px', padding: '12px 16px', display: 'flex', alignItems: 'center', gap: '12px', transition: 'all 0.15s' }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ color: '#ffffff', fontSize: '14px', fontWeight: '500' }}>{skill.name}</div>
                          {skill.description && <div style={{ color: '#9ca3af', fontSize: '12px', marginTop: '2px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{skill.description}</div>}
                        </div>
                        <button onClick={(e) => handleReadSkill(skill, e)} disabled={loadingSkillContent === skill.id} style={{ background: 'transparent', border: '1px solid #4b5563', borderRadius: '5px', color: '#9ca3af', cursor: 'pointer', padding: '3px 8px', fontSize: '11px', display: 'flex', alignItems: 'center', gap: '3px', flexShrink: 0 }}>
                          <BookOpen size={11} /> {loadingSkillContent === skill.id ? '...' : 'Lire'}
                        </button>
                        {locked ? (
                          <div style={{ flexShrink: 0, display: 'flex', alignItems: 'center', gap: '4px', color: '#f59e0b', fontSize: '11px' }}>
                            <Lock size={13} /> Socle
                          </div>
                        ) : (
                          <button onClick={() => toggleSkill(skill.id)} style={{ flexShrink: 0, width: '44px', height: '24px', borderRadius: '12px', border: 'none', cursor: 'pointer', backgroundColor: isEnabled ? '#10b981' : '#4b5563', position: 'relative', transition: 'background-color 0.2s' }}>
                            <span style={{ position: 'absolute', top: '3px', left: isEnabled ? '23px' : '3px', width: '18px', height: '18px', borderRadius: '50%', backgroundColor: '#ffffff', transition: 'left 0.2s' }} />
                          </button>
                        )}
                      </div>
                    )
                  }

                  return (
                    <>
                      {socleSkills.length > 0 && (
                        <div>
                          <div style={{ color: '#f59e0b', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Lock size={11} /> Socle — chargé dans tous les projets
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {socleSkills.map(skill => renderSkillRow(skill, true))}
                          </div>
                        </div>
                      )}
                      {Object.entries(grouped).map(([category, skills]) => (
                        <div key={category}>
                          <div style={{ color: '#6b7280', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', fontWeight: '600' }}>
                            {category}
                          </div>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                            {skills.map(skill => renderSkillRow(skill, false))}
                          </div>
                        </div>
                      ))}
                    </>
                  )
                })()}
              </div>
            )}
          </div>
        )}

        {activeTab === 'context' && (
          <div style={{ backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <Folder size={22} style={{ color: '#ec4899' }} /> Explorateur de Fichiers du Projet
              </h2>

              {/* Input caché partagé par les deux boutons d'import */}
              <input ref={fileInputRef} type="file" style={{ display: 'none' }} onChange={handleFileSelected} />

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => triggerImport(false)}
                  disabled={uploading}
                  title="Déposer un fichier (PDF, DOCX, image…) UNIQUEMENT dans le dossier du projet (pas dans la recherche Knowledge)"
                  style={{ backgroundColor: '#4b5563', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: uploading ? 'not-allowed' : 'pointer', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', opacity: uploading ? 0.5 : 1 }}
                >
                  <FileUp size={16} /> Importer (dossier)
                </button>
                <button
                  onClick={() => triggerImport(true)}
                  disabled={uploading}
                  title="Déposer un fichier dans le dossier ET l'indexer dans Knowledge (RAG), tagué à ce projet"
                  style={{ backgroundColor: '#7c3aed', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: uploading ? 'not-allowed' : 'pointer', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', opacity: uploading ? 0.5 : 1 }}
                >
                  {uploading ? <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} /> : <BookOpen size={16} />} Importer + Knowledge (RAG)
                </button>

              {!showNewFileInput ? (
                <button
                  onClick={() => setShowNewFileNameInput(true)}
                  style={{ backgroundColor: '#ec4899', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: 'pointer', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', transition: 'background-color 0.2s' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#db2777'}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ec4899'}
                >
                  <Plus size={16} /> Nouveau fichier
                </button>
              ) : (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <input 
                    type="text" 
                    placeholder="ex: notes.md, config.json" 
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    style={{ backgroundColor: '#1f2937', color: '#ffffff', border: '1px solid #4b5563', borderRadius: '6px', padding: '6px 12px', fontSize: '14px', outline: 'none' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreateFile() }}
                  />
                  <button 
                    onClick={handleCreateFile}
                    style={{ backgroundColor: '#10b981', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: 'pointer', padding: '6px 12px', fontSize: '14px', fontWeight: '500' }}
                  >
                    Créer
                  </button>
                  <button 
                    onClick={() => { setShowNewFileNameInput(false); setNewFileName(''); }}
                    style={{ backgroundColor: 'transparent', border: '1px solid #4b5563', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', padding: '6px 12px', fontSize: '14px' }}
                  >
                    Annuler
                  </button>
                </div>
              )}
              </div>
            </div>

            {/* Split Panel Layout */}
            <div style={{ display: 'grid', gridTemplateColumns: '300px 1fr', gap: '20px', minHeight: '500px' }}>
              
              {/* Left Pane - File List */}
              <div style={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', display: 'flex', flexDirection: 'column', gap: '8px', overflowY: 'auto', maxHeight: '600px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '8px' }}>
                  Fichiers du projet
                </span>
                
                {projectFiles.length === 0 ? (
                  <div style={{ color: '#9ca3af', fontSize: '14px', textAlign: 'center', padding: '20px' }}>
                    Aucun fichier disponible
                  </div>
                ) : (
                  projectFiles.map((file) => {
                    const isSelected = selectedFilePath === file.path
                    return (
                      <div
                        key={file.path}
                        onClick={() => handleSelectFile(file.path)}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '10px 12px',
                          borderRadius: '6px',
                          backgroundColor: isSelected ? '#ec489920' : 'transparent',
                          border: isSelected ? '1px solid #ec4899' : '1px solid transparent',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          color: isSelected ? '#ffffff' : '#d1d5db'
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = '#374151'
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = 'transparent'
                          }
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', overflow: 'hidden' }}>
                          <FileText size={18} style={{ color: isSelected ? '#ec4899' : '#9ca3af', flexShrink: 0 }} />
                          <div style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            <div style={{ fontSize: '14px', fontWeight: '500' }}>{file.path}</div>
                            <div style={{ fontSize: '11px', color: '#9ca3af' }}>{(file.size / 1024).toFixed(1)} KB</div>
                          </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', gap: '2px', flexShrink: 0 }}>
                          <button
                            onClick={(e) => handleDownloadFile(file.path, e)}
                            disabled={downloadingFile === file.path}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#9ca3af',
                              cursor: 'pointer',
                              padding: '4px',
                              borderRadius: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'all 0.2s',
                              opacity: downloadingFile === file.path ? 0.5 : 1
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#374151'; e.currentTarget.style.color = '#ffffff' }}
                            onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#9ca3af' }}
                            title="Télécharger le fichier"
                          >
                            <Download size={15} />
                          </button>

                          {file.path !== 'CONTEXT.md' && file.path !== '.project.json' && (
                            <button
                              onClick={(e) => handleDeleteFile(file.path, e)}
                              disabled={deletingFile === file.path}
                              style={{
                                background: 'none',
                                border: 'none',
                                color: '#ef4444',
                                cursor: 'pointer',
                                padding: '4px',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'background-color 0.2s',
                                opacity: deletingFile === file.path ? 0.5 : 1
                              }}
                              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#7f1d1d'}
                              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                              title="Supprimer le fichier"
                            >
                              <Trash2 size={15} />
                            </button>
                          )}
                        </div>
                      </div>
                    )
                  })
                )}
              </div>

              {/* Right Pane - Text Editor */}
              <div style={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', padding: '20px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {selectedFilePath ? (
                  <>
                    {/* Editor Header */}
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid #374151', paddingBottom: '12px' }}>
                      <div>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#ffffff', margin: '0 0 4px 0' }}>
                          {previewKind ? 'Aperçu' : 'Édition'} : {selectedFilePath}
                        </h3>
                        <span style={{ fontSize: '12px', color: '#9ca3af' }}>
                          {previewKind === 'text' ? 'Texte extrait (lecture seule) — ⬇ pour le fichier original'
                            : previewKind ? 'Fichier binaire (lecture seule) — ⬇ pour télécharger'
                            : 'Format texte UTF-8 (MD, JSON, TXT, YAML)'}
                        </span>
                      </div>

                      {fileStatus.type && (
                        <div style={{
                          fontSize: '13px',
                          fontWeight: '500',
                          color: fileStatus.type === 'success' ? '#34d399' : '#fca5a5'
                        }}>
                          {fileStatus.message}
                        </div>
                      )}
                    </div>

                    {/* Editor Body */}
                    {loadingFileContent ? (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ec4899' }}>
                        <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                      </div>
                    ) : previewKind === 'image' ? (
                      <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#111827', borderRadius: '8px', border: '1px solid #4b5563', padding: '12px', minHeight: '350px', overflow: 'auto' }}>
                        <img src={previewUrl || ''} alt={selectedFilePath || ''} style={{ maxWidth: '100%', maxHeight: '540px', objectFit: 'contain' }} />
                      </div>
                    ) : previewKind === 'pdf' ? (
                      <iframe src={previewUrl || ''} title={selectedFilePath || ''} style={{ flex: 1, width: '100%', minHeight: '540px', border: '1px solid #4b5563', borderRadius: '8px', backgroundColor: '#111827' }} />
                    ) : previewKind === 'text' ? (
                      <textarea
                        value={extractedText}
                        readOnly
                        placeholder="(aucun texte extrait)"
                        style={{
                          flex: 1,
                          backgroundColor: '#111827',
                          color: '#e5e7eb',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #4b5563',
                          resize: 'none',
                          lineHeight: '1.6',
                          outline: 'none',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                          minHeight: '350px'
                        }}
                      />
                    ) : (
                      <textarea
                        value={fileContent}
                        onChange={(e) => setFileContent(e.target.value)}
                        placeholder="# Nouveau Fichier..."
                        style={{
                          flex: 1,
                          backgroundColor: '#111827',
                          color: '#e5e7eb',
                          fontFamily: 'monospace',
                          fontSize: '14px',
                          padding: '16px',
                          borderRadius: '8px',
                          border: '1px solid #4b5563',
                          resize: 'none',
                          lineHeight: '1.6',
                          outline: 'none',
                          boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)',
                          minHeight: '350px'
                        }}
                      />
                    )}

                    {/* Editor Footer (masqué en aperçu / lecture seule) */}
                    {!previewKind && (
                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', borderTop: '1px solid #374151', paddingTop: '12px' }}>
                      <button
                        onClick={handleSaveFile}
                        disabled={savingFile || loadingFileContent}
                        style={{
                          padding: '8px 20px',
                          borderRadius: '6px',
                          backgroundColor: '#ec4899',
                          color: '#ffffff',
                          border: 'none',
                          cursor: (savingFile || loadingFileContent) ? 'not-allowed' : 'pointer',
                          fontWeight: '600',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          opacity: (savingFile || loadingFileContent) ? 0.6 : 1,
                          transition: 'all 0.2s',
                          boxShadow: '0 4px 6px -1px rgba(236, 72, 153, 0.3)'
                        }}
                        onMouseEnter={(e) => {
                          if (!savingFile && !loadingFileContent) e.currentTarget.style.backgroundColor = '#db2777'
                        }}
                        onMouseLeave={(e) => {
                          if (!savingFile && !loadingFileContent) e.currentTarget.style.backgroundColor = '#ec4899'
                        }}
                      >
                        <Save size={16} />
                        {savingFile ? 'Enregistrement...' : 'Enregistrer'}
                      </button>
                    </div>
                    )}
                  </>
                ) : (
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', gap: '12px' }}>
                    <Folder size={48} style={{ color: '#4b5563' }} />
                    <p style={{ margin: 0, fontSize: '15px' }}>
                      Sélectionnez un fichier dans la liste de gauche pour l'éditer.
                    </p>
                    <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
                      Vous pouvez également créer de nouveaux fichiers Markdown ou JSON dans ce projet.
                    </p>
                  </div>
                )}
              </div>

            </div>
          </div>
        )}

        {activeTab === 'journal' && (
          <div style={{ backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '12px', padding: '24px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <ClipboardList size={22} style={{ color: '#ec4899' }} /> Journal
              </h2>
              <button onClick={() => setShowNewEntryForm(!showNewEntryForm)} style={{ backgroundColor: '#ec4899', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: 'pointer', padding: '8px 16px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', transition: 'background-color 0.2s' }} onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#db2777'} onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#ec4899'}>
                <Plus size={16} /> Nouvelle entrée
              </button>
            </div>

            {showNewEntryForm && (
              <div style={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                <input type="text" placeholder="Titre de l'entrée" value={newEntry.titre} onChange={(e) => setNewEntry({ ...newEntry, titre: e.target.value })} style={{ width: '100%', backgroundColor: '#111827', border: '1px solid #4b5563', borderRadius: '6px', color: '#ffffff', padding: '10px', fontSize: '14px', marginBottom: '12px' }} />
                <select value={newEntry.statut} onChange={(e) => setNewEntry({ ...newEntry, statut: e.target.value as any })} style={{ width: '100%', backgroundColor: '#111827', border: '1px solid #4b5563', borderRadius: '6px', color: '#ffffff', padding: '10px', fontSize: '14px', marginBottom: '12px' }}>
                  <option value="en_cours">En cours</option>
                  <option value="terminé">Terminé</option>
                  <option value="bloqué">Bloqué</option>
                </select>
                <textarea placeholder="Contenu (markdown)" value={newEntry.contenu} onChange={(e) => setNewEntry({ ...newEntry, contenu: e.target.value })} style={{ width: '100%', backgroundColor: '#111827', border: '1px solid #4b5563', borderRadius: '6px', color: '#ffffff', padding: '10px', fontSize: '14px', fontFamily: 'monospace', minHeight: '150px', marginBottom: '12px', resize: 'vertical' }} />
                <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                  <button onClick={() => { setShowNewEntryForm(false); setNewEntry({ titre: '', statut: 'en_cours', contenu: '' }); }} style={{ backgroundColor: 'transparent', border: '1px solid #4b5563', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', padding: '8px 16px', fontSize: '14px' }}>Annuler</button>
                  <button onClick={handleCreateJournalEntry} disabled={creatingEntry} style={{ backgroundColor: '#ec4899', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: 'pointer', padding: '8px 16px', fontSize: '14px', fontWeight: '500', opacity: creatingEntry ? 0.5 : 1 }}>
                    {creatingEntry ? 'Création...' : 'Créer'}
                  </button>
                </div>
              </div>
            )}

            {selectedEntry ? (
              <div style={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
                  <div>
                    <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#ffffff', margin: '0 0 8px 0' }}>{selectedEntry.entry.titre}</h3>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <span style={{ color: '#9ca3af', fontSize: '13px' }}>{selectedEntry.entry.date}</span>
                      {getStatusBadge(selectedEntry.entry.statut)}
                    </div>
                  </div>
                  <button onClick={() => setSelectedEntry(null)} style={{ backgroundColor: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '4px' }}>
                    <X size={20} />
                  </button>
                </div>
                <pre style={{ backgroundColor: '#111827', border: '1px solid #374151', borderRadius: '6px', padding: '16px', color: '#d1d5db', fontSize: '14px', fontFamily: 'monospace', lineHeight: '1.6', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                  {selectedEntry.contenu}
                </pre>
              </div>
            ) : journalEntries.length === 0 ? (
              <p style={{ color: '#9ca3af', margin: 0 }}>Aucune entrée de journal</p>
            ) : (
              <div style={{ display: 'grid', gap: '10px' }}>
                {journalEntries.map((entry) => (
                  <div key={entry.slug} onClick={() => handleSelectJournalEntry(entry)} style={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', padding: '14px', cursor: 'pointer', transition: 'all 0.2s' }} onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#ec4899'; e.currentTarget.style.transform = 'translateX(4px)'; }} onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#4b5563'; e.currentTarget.style.transform = 'translateX(0)'; }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                      <div style={{ flex: 1 }}>
                        <h4 style={{ fontSize: '15px', fontWeight: '500', color: '#ffffff', margin: '0 0 6px 0' }}>{entry.titre}</h4>
                        <span style={{ color: '#9ca3af', fontSize: '13px' }}>{entry.date}</span>
                      </div>
                      {getStatusBadge(entry.statut)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'notes' && (
          <div style={{ backgroundColor: '#374151', border: '1px solid #4b5563', borderRadius: '12px', padding: '24px', display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#ffffff', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
                <StickyNote size={22} style={{ color: '#ec4899' }} /> Bloc-notes
              </h2>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                {notesStatus === 'success' && <span style={{ fontSize: '13px', color: '#34d399' }}>✓ Sauvegardé</span>}
                {notesStatus === 'error' && <span style={{ fontSize: '13px', color: '#fca5a5' }}>⚠️ Erreur</span>}
                <button
                  onClick={saveNotes}
                  disabled={notesSaving || notesLoading}
                  style={{ backgroundColor: '#ec4899', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: (notesSaving || notesLoading) ? 'not-allowed' : 'pointer', padding: '8px 18px', display: 'flex', alignItems: 'center', gap: '6px', fontSize: '14px', fontWeight: '500', opacity: (notesSaving || notesLoading) ? 0.6 : 1, transition: 'background-color 0.2s' }}
                  onMouseEnter={(e) => { if (!notesSaving && !notesLoading) e.currentTarget.style.backgroundColor = '#db2777' }}
                  onMouseLeave={(e) => { if (!notesSaving && !notesLoading) e.currentTarget.style.backgroundColor = '#ec4899' }}
                >
                  <Save size={15} /> {notesSaving ? 'Enregistrement...' : 'Enregistrer'}
                </button>
              </div>
            </div>

            {notesLoading ? (
              <div style={{ display: 'flex', justifyContent: 'center', padding: '60px' }}>
                <Loader size={28} style={{ color: '#ec4899', animation: 'spin 1s linear infinite' }} />
              </div>
            ) : (
              <textarea
                value={notesContent}
                onChange={(e) => setNotesContent(e.target.value)}
                placeholder={'# Notes — ' + (contextBundle.config.name || projectName) + '\n\nÉcris ici tes notes, liens, évolutions, idées...\n\nSupporte le Markdown.'}
                style={{ backgroundColor: '#111827', border: '1px solid #4b5563', borderRadius: '8px', color: '#e5e7eb', fontFamily: 'monospace', fontSize: '14px', lineHeight: '1.7', minHeight: '480px', outline: 'none', padding: '20px', resize: 'vertical', boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.3)' }}
              />
            )}
            <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>
              Stocké dans <code style={{ color: '#9ca3af' }}>notes.md</code> dans le dossier du projet · chargeable par un agent si besoin
            </p>
          </div>
        )}

        {/* Modale lecture + édition skill */}
        {expandedSkill && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1001, padding: '20px' }} onClick={() => { setExpandedSkill(null); setSkillEditMode(false) }}>
            <div style={{ backgroundColor: '#1f2937', borderRadius: '12px', maxWidth: '800px', width: '100%', maxHeight: '85vh', display: 'flex', flexDirection: 'column', border: '1px solid #4b5563' }} onClick={e => e.stopPropagation()}>
              <div style={{ padding: '16px 20px', borderBottom: '1px solid #4b5563', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px' }}>
                <h2 style={{ fontSize: '17px', fontWeight: '600', color: '#ec4899', margin: 0, flex: 1 }}>{expandedSkill.name}</h2>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  {skillSaveStatus === 'success' && <span style={{ fontSize: '12px', color: '#34d399' }}>✓ Sauvegardé</span>}
                  {skillSaveStatus === 'error' && <span style={{ fontSize: '12px', color: '#fca5a5' }}>⚠️ Erreur</span>}
                  {!skillEditMode ? (
                    <button onClick={() => { setSkillEditMode(true); setSkillEditContent(expandedSkill.content) }}
                      style={{ background: '#374151', border: '1px solid #4b5563', borderRadius: '6px', color: '#d1d5db', cursor: 'pointer', padding: '5px 12px', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                      <Edit2 size={13} /> Modifier
                    </button>
                  ) : (
                    <>
                      <button onClick={handleSaveSkillContent} disabled={savingSkillContent}
                        style={{ background: '#10b981', border: 'none', borderRadius: '6px', color: '#fff', cursor: 'pointer', padding: '5px 14px', fontSize: '13px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '5px', opacity: savingSkillContent ? 0.6 : 1 }}>
                        <Save size={13} /> {savingSkillContent ? '...' : 'Sauvegarder'}
                      </button>
                      <button onClick={() => setSkillEditMode(false)}
                        style={{ background: 'transparent', border: '1px solid #4b5563', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', padding: '5px 10px', fontSize: '13px' }}>
                        Annuler
                      </button>
                    </>
                  )}
                  <button onClick={() => { setExpandedSkill(null); setSkillEditMode(false) }} style={{ background: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '4px' }}><X size={22} /></button>
                </div>
              </div>
              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                {skillEditMode ? (
                  <textarea
                    value={skillEditContent}
                    onChange={e => setSkillEditContent(e.target.value)}
                    style={{ width: '100%', minHeight: '380px', background: '#111827', border: '1px solid #4b5563', borderRadius: '8px', padding: '16px', color: '#d1d5db', fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.7', resize: 'vertical', boxSizing: 'border-box', outline: 'none' }}
                  />
                ) : (
                  <pre style={{ background: '#111827', border: '1px solid #374151', borderRadius: '8px', padding: '16px', color: '#d1d5db', fontSize: '13px', fontFamily: 'monospace', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {expandedSkill.content}
                  </pre>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Modale ajouter skill */}
        {showSkillModal && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px' }} onClick={handleCloseSkillModal}>
            <div style={{ backgroundColor: '#374151', borderRadius: '12px', maxWidth: '700px', width: '100%', maxHeight: '80vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={(e) => e.stopPropagation()}>
              <div style={{ padding: '20px', borderBottom: '1px solid #4b5563', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#ffffff', margin: 0 }}>Ajouter une skill</h2>
                <button onClick={handleCloseSkillModal} style={{ backgroundColor: 'transparent', border: 'none', color: '#9ca3af', cursor: 'pointer', padding: '4px' }}>
                  <X size={24} />
                </button>
              </div>

              <div style={{ display: 'flex', gap: '4px', padding: '12px 20px', borderBottom: '1px solid #4b5563' }}>
                <button onClick={() => setModalTab('library')} style={{ backgroundColor: modalTab === 'library' ? '#1f2937' : 'transparent', border: 'none', color: modalTab === 'library' ? '#ec4899' : '#9ca3af', cursor: 'pointer', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <BookOpen size={16} /> Bibliothèque
                </button>
                <button onClick={() => setModalTab('import')} style={{ backgroundColor: modalTab === 'import' ? '#1f2937' : 'transparent', border: 'none', color: modalTab === 'import' ? '#ec4899' : '#9ca3af', cursor: 'pointer', padding: '8px 16px', borderRadius: '6px', fontSize: '14px', fontWeight: '500', display: 'flex', alignItems: 'center', gap: '6px' }}>
                  <FileUp size={16} /> Importer
                </button>
              </div>

              <div style={{ flex: 1, overflowY: 'auto', padding: '20px' }}>
                {modalTab === 'library' ? (
                  loadingSkills ? (
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                      <Loader size={24} style={{ color: '#ec4899', animation: 'spin 1s linear infinite' }} />
                    </div>
                  ) : (
                    <div style={{ display: 'grid', gap: '8px' }}>
                      {availableSkills.map((skill) => {
                        const isEnabled = contextBundle?.config.skills_enabled?.includes(skill.id)
                        const isSelected = selectedSkillIds.has(skill.id)
                        return (
                          <label key={skill.id} style={{ backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '8px', padding: '12px', cursor: isEnabled ? 'not-allowed' : 'pointer', opacity: isEnabled ? 0.5 : 1, display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                            <input type="checkbox" checked={isSelected} onChange={(e) => {
                              if (isEnabled) return
                              const newSet = new Set(selectedSkillIds)
                              if (e.target.checked) newSet.add(skill.id)
                              else newSet.delete(skill.id)
                              setSelectedSkillIds(newSet)
                            }} disabled={isEnabled} style={{ marginTop: '2px', accentColor: '#ec4899' }} />
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: '15px', fontWeight: '500', color: '#ffffff', marginBottom: '4px' }}>{skill.name}</div>
                              {skill.description && <div style={{ fontSize: '13px', color: '#9ca3af' }}>{skill.description}</div>}
                              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '6px' }}>
                                {skill.category && <span style={{ backgroundColor: '#ec489920', color: '#ec4899', fontSize: '11px', padding: '2px 8px', borderRadius: '4px' }}>{skill.category}</span>}
                                {isEnabled && <span style={{ color: '#86efac', fontSize: '11px' }}>✓ Déjà activée</span>}
                              </div>
                            </div>
                          </label>
                        )
                      })}
                    </div>
                  )
                ) : (
                  <div>
                    <div style={{ border: '2px dashed #4b5563', borderRadius: '8px', padding: '32px', textAlign: 'center', marginBottom: '16px', backgroundColor: '#1f2937' }} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); if (e.dataTransfer.files[0]) setImportFile(e.dataTransfer.files[0]); }}>
                      <Upload size={40} style={{ color: '#9ca3af', margin: '0 auto 12px' }} />
                      <p style={{ color: '#d1d5db', margin: '0 0 8px 0', fontSize: '15px' }}>Glissez un fichier .md ici</p>
                      <p style={{ color: '#9ca3af', margin: '0 0 12px 0', fontSize: '13px' }}>ou</p>
                      <label style={{ backgroundColor: '#ec4899', color: '#ffffff', padding: '8px 16px', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: '500', display: 'inline-block' }}>
                        Parcourir
                        <input type="file" accept=".md" onChange={(e) => setImportFile(e.target.files?.[0] || null)} style={{ display: 'none' }} />
                      </label>
                      {importFile && <div style={{ marginTop: '12px', color: '#86efac', fontSize: '14px' }}>✓ {importFile.name}</div>}
                    </div>
                    <input type="text" placeholder="Nom de la skill" value={importName} onChange={(e) => setImportName(e.target.value)} style={{ width: '100%', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '6px', color: '#ffffff', padding: '10px', fontSize: '14px', marginBottom: '12px' }} />
                    <select value={importCategory} onChange={(e) => setImportCategory(e.target.value)} style={{ width: '100%', backgroundColor: '#1f2937', border: '1px solid #4b5563', borderRadius: '6px', color: '#ffffff', padding: '10px', fontSize: '14px', marginBottom: '12px' }}>
                      <option value="Système">Système</option>
                      <option value="Code">Code</option>
                      <option value="Metal/Musique">Metal/Musique</option>
                    </select>
                  </div>
                )}
              </div>

              <div style={{ padding: '16px 20px', borderTop: '1px solid #4b5563', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={handleCloseSkillModal} style={{ backgroundColor: 'transparent', border: '1px solid #4b5563', borderRadius: '6px', color: '#9ca3af', cursor: 'pointer', padding: '8px 16px', fontSize: '14px' }}>Annuler</button>
                <button onClick={modalTab === 'library' ? handleAddSkills : handleImportSkill} disabled={modalTab === 'library' ? loadingSkills : importing} style={{ backgroundColor: '#ec4899', border: 'none', borderRadius: '6px', color: '#ffffff', cursor: 'pointer', padding: '8px 16px', fontSize: '14px', fontWeight: '500', opacity: (modalTab === 'library' ? loadingSkills : importing) ? 0.5 : 1 }}>
                  {modalTab === 'library' ? (loadingSkills ? 'Ajout...' : 'Valider') : (importing ? 'Import...' : 'Importer')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
